import styled, { css, keyframes } from 'styled-components';

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(28px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-8px);
  }
`;

const staggered = css`
  animation: ${fadeUp} 0.65s ease both;
  animation-delay: ${({ delay }) => (delay ? `${delay * 0.08}s` : '0s')};
`;

export const LandingPage = styled.div`
  --landing-navy: #043873;
  --landing-blue: #5484c3;
  --landing-blue-dark: #3869a9;
  --landing-blue-pale: #eff6ff;
  --landing-accent: #f59e0b;
  --landing-text: #1e293b;
  --landing-muted: #64748b;
  --landing-border: #e2e8f0;

  width: 100%;
  overflow-x: hidden;
  background: #fff;
  color: var(--landing-text);
  font-family: 'DM Sans', Helvetica, Arial, sans-serif;

  a {
    text-decoration: none;
  }
`;

export const Hero = styled.section`
  position: relative;
  min-height: calc(100vh - 80px);
  overflow: hidden;
  background: linear-gradient(135deg, #f0f6ff 0%, #ffffff 58%, #e8f4fd 100%);

  @media (max-width: 800px) {
    min-height: calc(100vh - 64px);
  }
`;

export const HeroSlide = styled.div`
  display: ${({ active }) => (active ? 'grid' : 'none')};
  grid-template-columns: minmax(0, 1fr) minmax(320px, 1fr);
  align-items: center;
  gap: 56px;
  min-height: calc(100vh - 80px);
  padding: 78px 5% 102px;
  position: relative;
  z-index: 1;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 38px;
    padding: 62px 5% 88px;
  }

  @media (max-width: 800px) {
    min-height: calc(100vh - 64px);
    gap: 24px;
    padding: 32px 5% 86px;
  }
`;

export const HeroCopy = styled.div`
  ${staggered}
  width: 100%;
  max-width: 640px;
  text-align: left;
  letter-spacing: 0;
  word-spacing: normal;

  h1 {
    max-width: 620px;
    margin: 0 0 22px;
    color: var(--landing-navy);
    font-family: 'Sora', Helvetica, Arial, sans-serif;
    font-size: clamp(2.35rem, 4vw, 3.55rem);
    font-weight: 800;
    line-height: 1.14;
    text-align: left;
    letter-spacing: 0;
    word-spacing: normal;
  }

  p {
    max-width: 500px;
    width: 100%;
    margin: 0 0 34px;
    color: var(--landing-muted);
    font-size: 1.05rem;
    line-height: 1.75;
    text-align: left;
    letter-spacing: 0;
    word-spacing: normal;
    white-space: normal;
    overflow-wrap: break-word;
  }

  @media (max-width: 1024px) {
    text-align: center;

    h1,
    p {
      margin-left: auto;
      margin-right: auto;
      text-align: center;
    }
  }

  @media (max-width: 520px) {
    h1 {
      max-width: 290px;
      font-size: 2.05rem;
    }

    p {
      max-width: 280px;
      font-size: 0.98rem;
    }
  }
`;

export const HeroButtons = styled.div`
  display: flex;
  gap: 14px;
  flex-wrap: wrap;

  @media (max-width: 1024px) {
    justify-content: center;
  }

  @media (max-width: 520px) {
    flex-direction: column;
    align-items: center;
  }
`;

export const LandingButton = styled.button`
  display: inline-flex;
  min-height: 46px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 0;
  border-radius: 8px;
  padding: 12px 26px;
  background: var(--landing-blue);
  color: #fff;
  box-shadow: 0 8px 20px rgba(84, 132, 195, 0.28);
  cursor: pointer;
  font: inherit;
  font-size: 0.98rem;
  font-weight: 700;
  transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  &:hover,
  &:focus {
    background: var(--landing-blue-dark);
    color: #fff;
    box-shadow: 0 12px 26px rgba(84, 132, 195, 0.34);
    transform: translateY(-1px);
  }

  @media (max-width: 520px) {
    width: min(100%, 270px);
  }
`;

export const OutlineButton = styled(LandingButton)`
  border: 2px solid var(--landing-blue);
  background: transparent;
  color: var(--landing-blue);
  box-shadow: none;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover,
  &:focus {
    background: var(--landing-blue);
    color: #fff;
    box-shadow: 0 10px 24px rgba(84, 132, 195, 0.22);
  }
`;

export const HeroImage = styled.div`
  ${staggered}
  animation-delay: 0.12s;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    max-width: 620px;
    height: auto;
    border-radius: 8px;
    box-shadow: 0 18px 54px rgba(4, 56, 115, 0.18);
    object-fit: cover;
  }

  @media (max-width: 1024px) {
    img {
      max-width: 540px;
    }
  }

  @media (max-width: 720px) {
    order: -1;
    width: min(86vw, 330px);
    margin: 0 auto;

    img {
      max-width: 100%;
      height: clamp(150px, 42vw, 210px);
      object-fit: cover;
      object-position: center;
    }
  }
`;

export const HeroIndicators = styled.div`
  position: absolute;
  left: 50%;
  bottom: 30px;
  z-index: 2;
  display: flex;
  gap: 10px;
  transform: translateX(-50%);
`;

export const HeroDot = styled.button`
  width: ${({ active }) => (active ? '28px' : '10px')};
  height: 10px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: ${({ active }) => (active ? 'var(--landing-blue)' : '#c7d9f0')};
  cursor: pointer;
  transition: background 0.25s ease, width 0.25s ease;
`;

export const SectionShell = styled.section`
  padding: 82px 5%;
  background: ${({ tone }) => (tone === 'muted' ? '#f8fafc' : '#fff')};

  ${({ variant }) =>
    variant === 'testimonial' &&
    css`
      display: grid;
      grid-template-columns: minmax(220px, 0.75fr) minmax(0, 1.8fr);
      gap: 56px;
      align-items: center;
      background-position: center;
      background-repeat: no-repeat;
      background-size: cover;
    `}

  ${({ variant }) =>
    variant === 'stats' &&
    css`
      padding: 60px 5%;
      background-color: var(--landing-navy);
      background-position: left center;
      background-repeat: no-repeat;
      background-size: 200px auto;
    `}

  @media (max-width: 768px) {
    padding: 66px 5%;

    ${({ variant }) =>
      variant === 'testimonial' &&
      css`
        grid-template-columns: 1fr;
        text-align: center;
      `}
  }
`;

export const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 28px;
  max-width: 1100px;
  margin: 0 auto;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

export const FeatureCard = styled.article`
  ${staggered}
  min-height: 260px;
  padding: 38px 28px;
  border: 1px solid var(--landing-border);
  border-radius: 8px;
  background: #fff;
  text-align: center;
  transition: border-color 0.25s ease, box-shadow 0.25s ease,
    transform 0.25s ease;

  h3 {
    margin: 0 0 12px;
    color: var(--landing-navy);
    font-family: 'Sora', Helvetica, Arial, sans-serif;
    font-size: 1.08rem;
    font-weight: 800;
  }

  p {
    margin: 0;
    color: var(--landing-muted);
    font-size: 0.92rem;
    line-height: 1.7;
  }

  &:hover {
    border-color: #aac2ed;
    box-shadow: 0 18px 42px rgba(37, 99, 235, 0.12);
    transform: translateY(-4px);
  }
`;

export const FeatureIcon = styled.div`
  width: 60px;
  height: 60px;
  margin: 0 auto 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: var(--landing-blue-pale);

  svg {
    width: 28px;
    height: 28px;
    color: var(--landing-blue);
  }
`;

export const SectionHeader = styled.div`
  max-width: 620px;
  margin: 0 auto;
  text-align: center;

  h2 {
    margin: 0 0 12px;
    color: var(--landing-navy);
    font-family: 'Sora', Helvetica, Arial, sans-serif;
    font-size: clamp(1.65rem, 3vw, 2.3rem);
    font-weight: 800;
  }

  p {
    margin: 0;
    color: var(--landing-muted);
    font-size: 0.98rem;
    line-height: 1.75;
  }
`;

export const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 28px;
  max-width: 1100px;
  margin: 42px auto 0;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

export const BlogCard = styled.article`
  ${staggered}
  overflow: hidden;
  border: 1px solid rgba(226, 232, 240, 0.85);
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 3px 14px rgba(15, 23, 42, 0.06);
  transition: box-shadow 0.25s ease, transform 0.25s ease;

  &:hover {
    box-shadow: 0 18px 42px rgba(37, 99, 235, 0.13);
    transform: translateY(-4px);
  }
`;

export const BlogImage = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #dbeafe;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }
`;

export const BlogBody = styled.div`
  padding: 20px;
  text-align: left;
  text-align-last: left;
  word-spacing: normal;

  h3 {
    min-height: 68px;
    margin: 0 0 16px;
    color: var(--landing-navy);
    font-family: 'Sora', Helvetica, Arial, sans-serif;
    font-size: 0.98rem;
    font-weight: 800;
    line-height: 1.45;
    letter-spacing: 0;
    text-align: left;
    text-align-last: left;
    text-wrap: balance;
    word-spacing: normal;
  }

  a {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: var(--landing-blue);
    font-size: 0.9rem;
    font-weight: 800;
    transition: gap 0.2s ease;

    &:hover {
      gap: 10px;
      color: var(--landing-blue-dark);
    }
  }
`;

export const CtaBand = styled.section`
  padding: 82px 5%;
  background: #fff;
  text-align: center;

  h2 {
    max-width: 680px;
    margin: 0 auto 30px;
    color: var(--landing-navy);
    font-family: 'Sora', Helvetica, Arial, sans-serif;
    font-size: clamp(1.9rem, 4vw, 2.8rem);
    font-weight: 800;
    line-height: 1.2;
  }
`;

export const DemoButton = styled(LandingButton)`
  min-height: 52px;
  padding: 14px 38px;
`;

export const TestimonialPhoto = styled.div`
  ${staggered}
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;

  .photo-frame {
    width: 210px;
    height: 230px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 3px solid rgba(255, 255, 255, 0.16);
    border-radius: 8px;
    background: linear-gradient(135deg, #1e3a5f, #243552);

    svg {
      width: 84px;
      height: 84px;
      color: rgba(255, 255, 255, 0.34);
    }
  }

  .photo-label {
    margin-top: -14px;
    padding: 7px 16px;
    border-radius: 999px;
    background: #fff;
    color: var(--landing-navy);
    font-size: 0.82rem;
    font-weight: 800;
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.16);
  }

  .stars {
    display: flex;
    gap: 5px;
    margin-top: 18px;
    color: var(--landing-accent);

    svg {
      fill: currentColor;
    }
  }
`;

export const TestimonialContent = styled.div`
  ${staggered}
  animation-delay: 0.14s;
  color: #fff;

  .quote-mark {
    display: block;
    margin-bottom: 8px;
    color: rgba(255, 255, 255, 0.22);
    font-family: Georgia, serif;
    font-size: 5rem;
    line-height: 0.8;
  }

  blockquote {
    max-width: 820px;
    margin: 0 0 22px;
    color: rgba(255, 255, 255, 0.88);
    font-size: 1.12rem;
    font-style: italic;
    line-height: 1.8;
  }

  cite {
    color: rgba(255, 255, 255, 0.62);
    font-size: 0.94rem;
    font-style: normal;
  }

  @media (max-width: 768px) {
    blockquote {
      margin-left: auto;
      margin-right: auto;
    }
  }
`;

export const SecurityInner = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 0.9fr);
  gap: 70px;
  align-items: center;
  max-width: 1100px;
  margin: 0 auto;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
    gap: 42px;
  }
`;

export const SecurityText = styled.div`
  h2 {
    margin: 0 0 18px;
    color: var(--landing-navy);
    font-family: 'Sora', Helvetica, Arial, sans-serif;
    font-size: clamp(1.75rem, 3vw, 2.55rem);
    font-weight: 800;
    line-height: 1.12;
    letter-spacing: 0;

    span {
      display: inline-block;
      position: relative;
      color: var(--landing-blue);
      letter-spacing: 0;

      &::after {
        content: '';
        position: absolute;
        left: 0;
        bottom: -8px;
        width: 100%;
        height: 5px;
        border-radius: 999px;
        background: var(--landing-accent);
      }
    }
  }

  p {
    max-width: 760px;
    margin: 0 0 42px;
    color: var(--landing-muted);
    font-size: clamp(0.98rem, 1.15vw, 1.12rem);
    line-height: 1.75;
    letter-spacing: 0;
    word-spacing: normal;
    text-align: left;
  }

  @media (max-width: 820px) {
    h2 {
      font-size: clamp(1.7rem, 7vw, 2.25rem);
    }

    p {
      font-size: 1rem;
      line-height: 1.7;
    }
  }
`;

export const SecurityList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    align-items: center;
    gap: 12px;
    color: var(--landing-text);
    font-size: 0.96rem;
    font-weight: 700;
  }
`;

export const CheckIcon = styled.span`
  width: 24px;
  height: 24px;
  display: inline-flex;
  flex: 0 0 24px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: var(--landing-blue-pale);

  svg {
    width: 14px;
    height: 14px;
    color: var(--landing-blue);
  }
`;

export const SecurityDiagram = styled.div`
  position: relative;
  width: min(100%, 330px);
  height: 285px;
  margin: 0 auto;

  svg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    overflow: visible;

    line {
      stroke: #dbeafe;
      stroke-width: 1.7;
      stroke-dasharray: 5 4;
    }
  }

  .diagram-center {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 82px;
    height: 82px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 999px;
    background: var(--landing-blue);
    box-shadow: 0 0 0 12px rgba(84, 132, 195, 0.12),
      0 0 0 24px rgba(84, 132, 195, 0.06);
    color: #fff;
    transform: translate(-50%, -50%);

    .center-emoji {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      font-family: 'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji',
        sans-serif;
      font-size: 2rem;
      line-height: 1;
      text-align: center;
      transform: translateY(1px);
    }
  }

  @media (max-width: 620px) {
    display: none;
  }
`;

export const IconNode = styled.div`
  position: absolute;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--landing-border);
  border-radius: 999px;
  background: #fff;
  box-shadow: 0 10px 28px rgba(37, 99, 235, 0.12);
  color: var(--landing-blue);
  animation: ${float} 5s ease-in-out infinite;

  .node-emoji {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    font-family: 'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji',
      sans-serif;
    font-size: 1.45rem;
    line-height: 1;
    text-align: center;
    transform: translateY(1px);
  }

  &.top-left {
    top: 20px;
    left: 20px;
  }

  &.top-right {
    top: 20px;
    right: 20px;
    animation-delay: 0.8s;
  }

  &.bottom-left {
    bottom: 20px;
    left: 20px;
    animation-delay: 1.6s;
  }

  &.bottom-right {
    right: 20px;
    bottom: 20px;
    animation-delay: 2.2s;
  }
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  max-width: 1000px;
  margin: 0 auto;

  @media (max-width: 720px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const StatItem = styled.div`
  ${staggered}
  padding: 20px;
  border-right: 1px solid rgba(255, 255, 255, 0.12);
  text-align: center;

  &:last-child {
    border-right: 0;
  }

  .stat-value {
    display: block;
    margin-bottom: 8px;
    color: #fff;
    font-family: 'Sora', Helvetica, Arial, sans-serif;
    font-size: clamp(2rem, 4vw, 3rem);
    font-weight: 800;
    line-height: 1;
  }

  .stat-label {
    color: rgba(255, 255, 255, 0.62);
    font-size: 0.76rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  @media (max-width: 720px) {
    &:nth-child(2) {
      border-right: 0;
    }
  }
`;
